import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Center,
  Heading,
  HStack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { RiCheckLine, RiErrorWarningLine } from "@remixicon/react";
import { useEffect } from "react";

import { Restricted } from "@/app/components/Restricted";
import SlackHashLogo from "@/common/assets/slack-hash-logo.svg?react";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import { DocumentationRefButton } from "@/common/components/DocumentationReference";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import { compactFormattedDate } from "@/common/utils/datetime";
import { useSlackConnection } from "@/integration/data/useSlackConnection";
import { useSlackDeleteMutation } from "@/integration/data/useSlackDeleteMutation";
import useSlackConnectionUrl from "@/integration/hooks/useSlackConnectionUrl";
import { useFlag } from "@reflag/react-sdk";
import { SlackConnection } from "@reflag/shared/slackConnectionAPI";

const description =
  "Slack notifications for flag access and stage changes and end-user flag feedback.";

function AgentDescription() {
  const { isEnabled } = useFlag("ai-slack-agent");
  if (!isEnabled) return null;

  return (
    <Text as="span">
      {" "}
      Mention <strong>@Reflag</strong> in channels, threads, or direct messages
      to interact with your flags.
    </Text>
  );
}

function Title() {
  return (
    <HStack>
      <SlackHashLogo height="16" width="16" />
      <Heading fontWeight="medium" size="sm">
        Slack
      </Heading>
    </HStack>
  );
}

function Connected({ slackConnection }: { slackConnection: SlackConnection }) {
  const { mutate: deleteConnection, isPending: mutationIsLoading } =
    useSlackDeleteMutation();

  return (
    <VStack align="flex-start" spacing={4} w="full">
      <VStack align="flex-start" spacing={2} w="full">
        <HStack justify="space-between" w="full">
          <Title />
          <Badge colorScheme="green">
            <HStack spacing={1}>
              <RiCheckLine size={16} />
              <Text>
                Since {compactFormattedDate(slackConnection.createdAt)}
              </Text>
            </HStack>
          </Badge>
        </HStack>

        <Text color="dimmed" fontSize="sm" maxW="lg">
          {description}
          <AgentDescription />
        </Text>
      </VStack>

      <ButtonGroup alignSelf="flex-end">
        <DocumentationRefButton section="integrations/slack" />
        <Restricted role="admin">
          <Button
            isLoading={mutationIsLoading}
            size="sm"
            variant="outline"
            onClick={() => deleteConnection()}
          >
            Disconnect
          </Button>
        </Restricted>
      </ButtonGroup>
    </VStack>
  );
}

function ConnectButton({ label = "Connect" }: { label?: string }) {
  const connectionUrl = useSlackConnectionUrl();

  return (
    <Restricted role="admin">
      <Button as="a" href={connectionUrl} size="sm" variant="outline">
        {label}
      </Button>
    </Restricted>
  );
}

function NeedsReauthorization() {
  return (
    <VStack align="flex-start" spacing={2} w="full">
      <HStack justify="space-between" w="full">
        <Title />
        <Badge colorScheme="orange">
          <HStack spacing={1}>
            <RiErrorWarningLine size={16} />
            <Text fontSize="sm">Requires reauthorization</Text>
          </HStack>
        </Badge>
      </HStack>

      <HStack justify="space-between" w="full">
        <Text color="dimmed" fontSize="sm" maxWidth="lg">
          Reauthorize Slack to enable <strong>@Reflag</strong> mentions and
          direct messages. Existing notifications will continue to work.
        </Text>
        <ButtonGroup>
          <DocumentationRefButton section="integrations/slack" />
          <ConnectButton label="Reauthorize" />
        </ButtonGroup>
      </HStack>
    </VStack>
  );
}

function NotConnected({ isGuard = false }: { isGuard?: boolean }) {
  const toast = useErrorToast();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get("slack-error");
    if (!error) return;

    toast({
      title:
        error === "slack_workspace_already_connected"
          ? "This Slack workspace is already connected to another Reflag organization."
          : "The connection to Slack failed. Please try again.",
      status: "error",
    });
    const url = new URL(window.location.href);
    url.searchParams.delete("slack-error");
    window.history.replaceState(
      {},
      document.title,
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, [toast]);

  return (
    <VStack align="flex-start" spacing={2} w="full">
      <HStack justify="space-between" w="full">
        <Title />
        {isGuard && (
          <Badge colorScheme="orange">
            <HStack spacing={1}>
              <RiErrorWarningLine size={16} />
              <Text>Not connected</Text>
            </HStack>
          </Badge>
        )}
      </HStack>
      <HStack justify="space-between" w="full">
        <Text color="dimmed" fontSize="sm" maxWidth="lg">
          {description}
          <AgentDescription />
        </Text>

        <ButtonGroup alignSelf="flex-end">
          <DocumentationRefButton section="integrations/slack" />

          <ConnectButton />
        </ButtonGroup>
      </HStack>
    </VStack>
  );
}

type SlackIntegrationFormProps = {
  isGuard?: boolean;
};

export default function SlackIntegrationForm({
  isGuard = false,
}: SlackIntegrationFormProps) {
  const { data, isLoading } = useSlackConnection();
  const { isEnabled: isAiAgentEnabled } = useFlag("ai-slack-agent");

  return (
    <Card w="full">
      <CardBody w="full">
        {isLoading ? (
          <Center>
            <DelayedSpinner />
          </Center>
        ) : data && isAiAgentEnabled && data.needsReauthorization ? (
          <NeedsReauthorization />
        ) : data ? (
          <Connected slackConnection={data} />
        ) : (
          <NotConnected isGuard={isGuard} />
        )}
      </CardBody>
    </Card>
  );
}
